import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingsAPI } from '../services/api';
import './ScheduleMeeting.css';

const ScheduleMeeting = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [duration, setDuration] = useState(30);
  const [invitees, setInvitees] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!scheduledStart) {
      setError('Scheduled time is required');
      return;
    }

    const scheduledDate = new Date(scheduledStart);
    if (scheduledDate <= new Date()) {
      setError('Scheduled time must be in the future');
      return;
    }

    // Parse invitees
    const inviteeList = invitees
      .split(',')
      .map(email => email.trim())
      .filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

    setLoading(true);
    try {
      const result = await meetingsAPI.schedule({
        title,
        description,
        scheduledStart: scheduledDate.toISOString(),
        duration: parseInt(duration),
        invitees: inviteeList
      });

      if (result.success) {
        setSuccess('Meeting scheduled successfully! Invitations have been sent.');
        setTimeout(() => {
          navigate('/meetings');
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date/time (now + 5 minutes)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="schedule-meeting-page">
      <div className="schedule-header">
        <h1>Schedule Meeting</h1>
        <p>Plan a meeting and invite participants</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="schedule-content">
        <form onSubmit={handleSubmit} className="schedule-form">
          <div className="form-group">
            <label>Meeting Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Weekly Team Standup"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Meeting agenda or description..."
              rows={4}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date & Time *</label>
              <input
                type="datetime-local"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                min={getMinDateTime()}
                required
              />
            </div>

            <div className="form-group">
              <label>Duration (minutes)</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Invite Participants</label>
            <textarea
              value={invitees}
              onChange={(e) => setInvitees(e.target.value)}
              placeholder="Enter email addresses separated by commas&#10;john@example.com, jane@example.com"
              rows={3}
            />
            <small>Enter email addresses separated by commas. Invitations will be sent automatically.</small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Scheduling...' : 'Schedule Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleMeeting;
